import React from 'react';
import { buttonReset, color, font, radius, space, TAP, weight } from '../theme';
import Icon, { IconName } from './Icon';

interface ButtonProps {
  children: React.ReactNode;
  onClick?: () => void;
  // primary: filled blue; secondary: soft blue; ghost: blue text only
  variant?: 'primary' | 'secondary' | 'ghost';
  icon?: IconName;
  fullWidth?: boolean;
  disabled?: boolean;
  type?: 'button' | 'submit';
  style?: React.CSSProperties;
}

const VARIANTS = {
  primary: { background: color.primary, color: color.textOnPrimary },
  secondary: { background: color.primarySoft, color: color.primary },
  ghost: { background: 'transparent', color: color.primary },
};

const Button = ({ children, onClick, variant = 'secondary', icon, fullWidth, disabled, type = 'button', style }: ButtonProps) => (
  <button
    type={type}
    onClick={onClick}
    disabled={disabled}
    style={{
      ...buttonReset,
      ...VARIANTS[variant],
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: space.sm - 2,
      minHeight: TAP,
      padding: `0 ${space.lg + 2}px`,
      width: fullWidth ? '100%' : undefined,
      borderRadius: radius.lg,
      fontSize: font.label,
      fontWeight: weight.medium,
      opacity: disabled ? 0.5 : 1,
      cursor: disabled ? 'default' : 'pointer',
      ...style,
    }}
  >
    {icon && <Icon name={icon} size={18} />}
    {children}
  </button>
);

/* ── A link to another site, styled as a small button (protocols, documents, broadcasts) ── */

interface ButtonLinkProps {
  href: string;
  children: React.ReactNode;
  icon?: IconName;
  variant?: 'secondary' | 'neutral';
}

export const ButtonLink = ({ href, children, icon, variant = 'secondary' }: ButtonLinkProps) => (
  <a
    href={href}
    target="_blank"
    rel="noreferrer"
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: space.xs + 2,
      minHeight: 36,
      padding: `0 ${space.md}px`,
      borderRadius: radius.md,
      fontSize: font.small,
      fontWeight: weight.medium,
      textDecoration: 'none',
      color: variant === 'secondary' ? color.primary : color.text,
      background: variant === 'secondary' ? color.primarySoft : color.surfaceMuted,
    }}
  >
    {icon && <Icon name={icon} size={16} />}
    {children}
  </a>
);

/* ── A round icon-only button (favourite, share) ── */

interface IconButtonProps {
  icon: IconName;
  label: string;
  onClick: () => void;
  pressed?: boolean;
  // White icon on a translucent circle, for a colored header band
  onDark?: boolean;
}

export const IconButton = ({ icon, label, onClick, pressed, onDark = false }: IconButtonProps) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={label}
    aria-pressed={pressed}
    style={{
      ...buttonReset,
      width: TAP,
      height: TAP,
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      color: onDark ? color.textOnPrimary : pressed ? color.primary : color.textSecondary,
      background: onDark ? 'rgba(255,255,255,0.14)' : 'transparent',
    }}
  >
    <Icon name={icon} size={20} strokeWidth={2} />
  </button>
);

export default Button;
